// This file is used to map the fields for the Utah's NGL Goods and Services Form.
import { getNGLFieldNames } from '@/components/documents/Foxit/FormMapping/FieldNameProviders/NGLFieldNameProvider'
import { useCustomItemFields } from '@/components/documents/Foxit/FormMapping/composables/CustomItemFields'
import {
	MultipleItemsMethod,
	useFormFillHelper,
} from '@/components/documents/Foxit/FormMapping/composables/FormFillHelper'
import { useIdentitySetup } from '@/components/documents/Foxit/FormMapping/composables/IdentityFields'
import { useSupplementalFields } from '@/components/documents/Foxit/FormMapping/composables/SupplementalFields'
import {
	groupedTotal,
	useTotalFields,
} from '@/components/documents/Foxit/FormMapping/composables/TotalFields'
import type {
	GroupedTotalConfig,
	SectionTotalConfig,
} from '@/components/documents/Foxit/FormMapping/composables/TotalFields'
import { PbProductType } from '@/enums/PbProductType'
import type { CartItemAndLineItem } from '@/mixins/merchandise'
import type { CustomCartItem } from '@/types'

export default class NGLUTGoodsAndServicesForm {
	private fieldNameProvider = getNGLFieldNames()

	// Standard list of fields for this form.
	// These values point to field names in the fieldNameProvider, as well as functions to get the value for the field via the fieldValueProvider
	private fields: (keyof typeof this.fieldNameProvider)[] = [
		// Service
		'packageName',
		'packagePrice',
		'basicServicesDesc',
		'basicServicesPrice',
		'embalmingAndRefrigerationFields',
		'transferMileRadiusFields',
		'otherPrepFields',
		'funeralMemorialServicePrice',
		'visitationFields',
		'hearseFields',
		'familyCarLimoFields',
		'basicServicesAdditionalFields',
		'acknowledgeCardsFields',
		'memorialFoldersFields',
		'memorialBookFields',
		'additionalServicesFields',

		// Merchandise
		'casketFields',
		'outerBurialFields',
		'cremationAltContainerFields',
		'urnFields',
		'merchandiseOtherAndDiscountFields',
		'merchandiseOther2Fields',

		// Cash Advance
		// Special Scenario: Utah's G&S form has no fields for cash advance.
		'allCashAdvanceFields',
		'allCashAdvanceItemsOnSupplemental',

		// Identity
		'insuredFullName',
		'insuredFullAddress',
		'insuredSSN',
		'insuredDateOfBirth',
		'purchaserFullName',
		'funeralHomeRepresentativePrintedName',
		'funeralHomeName',
		'funeralHomeFullAddress',
		'dateFields',
	]

	private unfilledFields = [
		// signatures
		'signedDayOfMonth',
		'signedYear',
		'signedMonth',
		'purchaserSignature',
		'purchaserSignatureDate',
		'insuranceAgentSignature',
		'insuranceAgentSignatureDate',
		'preneedSignature',
		'preneedSignatureDate',
		'funeralHomeRepresentativeSignature',
		'funeralHomeRepresentativeSignatureDate',
		'notarySignature',
		'signedNotaryDayOfMonth',
		'signedNotaryMonth',
		'signedNotaryYear',
		// notary
		'notaryCounty',
		'notaryCommissionExpiresDate',
		// buyer rep
		'buyerRepFullName',
		'buyerRepSSN',
		'buyerRepDOB',
		'buyerRepAddress',
		'buyerRepCity',
		'buyerRepState',
		'buyerRepZip',
		'buyersRepInformWithContractCheck',
		'buyersRepInformNoContractCheck',
		'buyersRepNoInformCheck',
		// funeral home
		'funeralHomeCounty',
		'funeralPermitNumber',
		// license numbers
		'fdLicenseNumber',
		'preneedLicenseNumber',
		'insuranceAgentLicenseNumber',
		'contractNumber',
		// price list acknowledgement
		'priceListAcknowledgementDate',
		'priceListAcknowledgementInitials',
		// reason for embalming
		'reasonForEmbalmingDesc1',
		'reasonForEmbalmingDesc2',
	]
	private sectionTotals: SectionTotalConfig[] = [
		{ section: 'service' },
		{ section: 'merchandise' },
		{ section: 'cashAdvance' },
		{ section: 'header', emit: false },
	]
	private groupedTotals: GroupedTotalConfig[] = [
		groupedTotal('guaranteed', ['header']),
		groupedTotal('funeral', ['header']),
	]

	// Setup prior to building form filling array.
	constructor(planHolderId: string, cartItems?: CartItemAndLineItem[]) {
		const { identitySetup } = useIdentitySetup()
		const {
			setMultipleItemsMethod,
			setFieldNameProvider,
			setCartItems,
			setSectionOverrides,
			setCategoryOverrides,
		} = useFormFillHelper()

		identitySetup(planHolderId, ['planHolder', 'funeralHome', 'salesContact'])
		setMultipleItemsMethod(MultipleItemsMethod.Supplemental)
		setFieldNameProvider(this.fieldNameProvider)
		setSectionOverrides([
			{
				field: 'gAcknowledgeCardsDesc',
				section: 'service',
			},
			{
				field: 'gAcknowledgeCardsAmt',
				section: 'service',
			},
			{
				field: 'gMemorialFoldersDesc',
				section: 'service',
			},
			{
				field: 'gMemorialFoldersAmt',
				section: 'service',
			},
			{
				field: 'gMemorialBookDesc',
				section: 'service',
			},
			{
				field: 'gMemorialBookAmt',
				section: 'service',
			},
			{
				field: 'packagePrice',
				section: 'header',
			},
		])
		setCategoryOverrides([
			{
				field: 'gAcknowledgeCardsDesc',
				categories: [PbProductType.Service, PbProductType.Merchandise],
			},
			{
				field: 'gAcknowledgeCardsAmt',
				categories: [PbProductType.Service, PbProductType.Merchandise],
			},
			{
				field: 'gMemorialFoldersDesc',
				categories: [PbProductType.Service, PbProductType.Merchandise],
			},
			{
				field: 'gMemorialFoldersAmt',
				categories: [PbProductType.Service, PbProductType.Merchandise],
			},
			{
				field: 'gMemorialBookDesc',
				categories: [PbProductType.Service, PbProductType.Merchandise],
			},
			{
				field: 'gMemorialBookAmt',
				categories: [PbProductType.Service, PbProductType.Merchandise],
			},
		])

		if (cartItems) {
			setCartItems(cartItems)
		}
	}

	// Build form filling array.
	getFormMappings() {
		const { formatFields, mapFields, setFieldNameProvider } =
			useFormFillHelper()
		const { getTotalsFields } = useTotalFields()
		const { getSupplementalFields } = useSupplementalFields()

		setFieldNameProvider(this.fieldNameProvider)

		let mainFields = mapFields(this.fields)
		const supplementalFields = getSupplementalFields(mainFields)
		const totalFields = getTotalsFields(
			mainFields,
			this.sectionTotals,
			this.groupedTotals
		)

		return formatFields([...mainFields, ...supplementalFields, ...totalFields])
	}

	// Mappings for only the fields a custom item is filled into, so adding one
	// doesn't overwrite what the AFP entered by hand
	getCustomItemFormMappings(customItemId: CustomCartItem['id']) {
		const { getCustomItemFormMappings } = useCustomItemFields()

		return getCustomItemFormMappings(
			this.getFormMappings(),
			this.fields,
			customItemId
		)
	}
}
